import { useEffect, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import type { MessageDescriptor } from '@lingui/core';
import MaxBadge from './MaxBadge';
import UpgradeModal from './UpgradeModal';
import { accountService } from '../services/accountService';
import type { UpgradePromo } from '../services/accountService';

interface MaxUpsellProps {
  /** Feature name shown as the card title, e.g. msg`Privacy Agents`. */
  feature: MessageDescriptor;
  /** One line about what the feature does. */
  description: MessageDescriptor;
  /** Admins get the in-place upgrade; members are pointed at their admin. */
  isAdmin: boolean;
  /**
   * Which gate produced this panel, e.g. 'connections-paywall'. Threaded into
   * the checkout modal, which carries it onto the trial-call link so sales can
   * see which locked feature sent the customer.
   */
  source?: string;
}

/**
 * Shown in place of a Max-only page for free (Pro) users. EPIC-15 turned this
 * from a pointer at /subscription into the conversion surface itself: the
 * standing upgrade offer plus an in-place checkout (UpgradeModal). The API
 * 403 (MAX_TIER_REQUIRED) remains the real gate.
 *
 * Two ways through the gate, not one. An admin buying for themselves puts a
 * card in. An admin evaluating Max for a team hits this panel with no budget
 * approved yet, and the second button is for them: a trial code from sales,
 * or a call to get one.
 */
export default function MaxUpsell({ feature, description, isAdmin, source }: MaxUpsellProps) {
  const { i18n } = useLingui();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutPath, setCheckoutPath] = useState<'card' | 'trial'>('card');
  const [promo, setPromo] = useState<UpgradePromo>(null);

  // The promo line renders as soon as the config answers; the panel is
  // complete without it, so a slow or failed read costs nothing.
  useEffect(() => {
    let cancelled = false;
    accountService.getStripeConfig().then((cfg) => {
      if (!cancelled) setPromo(cfg.upgradePromo ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      className="glass-panel rounded-2xl p-8 sm:p-10 max-w-2xl mx-auto text-center"
      style={{
        border: '1px solid var(--color-max-border)',
        boxShadow: '0 8px 32px 0 var(--color-max-glow)',
      }}
    >
      <div className="flex justify-center mb-4">
        <MaxBadge />
      </div>
      <h2 className="text-xl sm:text-2xl font-semibold mb-2" style={{ color: 'var(--color-text)' }}>
        {i18n._(feature)}
      </h2>
      <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
        {i18n._(description)}
      </p>
      <p className="text-sm font-medium mb-2" style={{ color: 'var(--color-max-text)' }}>
        <Trans>Included with PrivacyPal Max</Trans>
      </p>
      {promo && promo.percentOff > 0 && (
        <p className="text-sm mb-6" style={{ color: 'var(--color-text-secondary)' }}>
          <Trans>
            Use code <span className="font-semibold" style={{ color: 'var(--color-max-text)' }}>{promo.code}</span> for {promo.percentOff}% off your first {promo.durationMonths} months.
          </Trans>
        </p>
      )}
      {isAdmin ? (
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={() => { setCheckoutPath('card'); setCheckoutOpen(true); }}
            className="px-6 py-2.5 text-sm font-semibold rounded-lg text-white transition-all hover:shadow-lg"
            style={{ backgroundColor: 'var(--color-max-accent)' }}
          >
            <Trans>Upgrade to Max</Trans>
          </button>
          <button
            onClick={() => { setCheckoutPath('trial'); setCheckoutOpen(true); }}
            className="px-6 py-2.5 text-sm font-semibold rounded-lg transition-all hover:shadow-lg"
            style={{ border: '1px solid var(--color-max-border)', color: 'var(--color-max-text)', backgroundColor: 'var(--color-max-bg)' }}
          >
            <Trans>Try Max company-wide</Trans>
          </button>
        </div>
      ) : (
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          <Trans>Ask your admin about PrivacyPal Max</Trans>
        </p>
      )}
      <UpgradeModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        source={source}
        initialPath={checkoutPath}
      />
    </div>
  );
}
